/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { captureException } from '@sentry/react'
import { useAddWorkflows } from 'queries/automations/useAddWorkflows'
import { useLoadAppointmentTypes } from 'queries/automations/useLoadAppointmentTypes'
import { useSyncAppointmentTypes } from 'queries/automations/useSyncAppointmentTypes'
import { useUpdateWorkflows } from 'queries/automations/useUpdateWorkflows'
import * as React from 'react'
import { toast } from 'react-toastify'
import type { TriggerOptions, Workflow } from 'types/automations'
import Button from 'ui/atoms/Button'
import Flex from 'ui/atoms/Flex'
import Heading from 'ui/atoms/Heading'
import Space from 'ui/atoms/Space'
import Text from 'ui/atoms/Text'
import Dropdown from 'ui/Dropdown'
import BlockList from 'ui/molecules/BlockList'
import TextField from 'ui/molecules/TextField'
import ToggleField from 'ui/molecules/ToggleField'
import NumericText from 'ui/NumericText'
import SelectGroup from 'ui/SelectGroup'
import { ZentakeContext } from 'utils/ZentakeContext'

import { QUERIES } from '@/enums/queries'
import { loadFormOptions } from '@/queries/forms/services/loadFormOptions'
import { loadPacketOptions } from '@/queries/packets/services/loadPacketOptions'
import { FormAsyncSelect } from '@/ui/form/antd/FormAsyncSelect'
import Modal from '@/ui/Modal'

import type { UpdateCreateWorkflow } from '../../../../types/automations'

type Props = {
  onClose: () => void
  calendarWorkflow: Workflow | null
}

function CreateEditAutomationModal({ onClose, calendarWorkflow }: Props) {
  const { data: appointmentTypes, isLoading: isAppointmentTypesLoading } =
    useLoadAppointmentTypes()
  const { updateWorkflow, isLoading: isUpdateWorkflowLoading } =
    useUpdateWorkflows()
  const { addWorkflow, isLoading: isAddWorkflowLoading } = useAddWorkflows()
  const { syncAppointmentType, isLoading: isSyncAppointmentTypesLoading } =
    useSyncAppointmentTypes()
  const calendarWorkflowsLoading =
    isUpdateWorkflowLoading || isAddWorkflowLoading
  const appointmentTypesLoading =
    isAppointmentTypesLoading || isSyncAppointmentTypesLoading
  const { updatePackets, updateForms } = React.useContext(ZentakeContext)

  const [name, setName] = React.useState(calendarWorkflow?.name || '')
  const [enabled, setEnabled] = React.useState(
    calendarWorkflow ? Boolean(calendarWorkflow?.enabled) : true
  )
  const [sendForm, setSendForm] = React.useState(
    calendarWorkflow
      ? !!calendarWorkflow.form_id || !!calendarWorkflow.document_id
      : true
  )
  const [form, setForm] = React.useState(calendarWorkflow?.form_id || null)
  const [document, setDocument] = React.useState(
    calendarWorkflow?.document_id || null
  )
  const [packet, setPacket] = React.useState(
    calendarWorkflow?.packet_id || null
  )
  const [useForAppointmentTypes, setUseForAppointmentTypes] = React.useState(
    calendarWorkflow
      ? calendarWorkflow.for_appointment_types?.map((type) => type.id)
      : []
  )
  const [send, setSend] = React.useState(
    calendarWorkflow
      ? calendarWorkflow.send_upon_scheduling
        ? 'immediately'
        : 'days_before_appointment'
      : null
  )
  const [sendBeforeDays, setSendBeforeDays] = React.useState(
    calendarWorkflow ? calendarWorkflow.send_before : null
  )
  const [sendAfterDays, setSendAfterDays] = React.useState(
    calendarWorkflow ? calendarWorkflow.send_after : null
  )
  const [trigger, setTrigger] = React.useState<TriggerOptions | null>(
    calendarWorkflow?.trigger || null
  )
  const [minDaysBetweenSends, setMinDaysBetweenSends] = React.useState(
    calendarWorkflow?.min_days_between_sends || null
  )
  const [errors, setErrors] = React.useState<{
    name?: string
    form?: string
    packet?: string
    useForAppointmentTypes?: string
    send?: string
    sendBeforeDays?: string
    sendAfterDays?: string
    trigger?: string
    minDaysBetweenSends?: string
  }>({
    name: '',
    form: '',
    packet: '',
    useForAppointmentTypes: '',
    send: '',
    sendBeforeDays: '',
    sendAfterDays: '',
    trigger: '',
    minDaysBetweenSends: '',
  })

  const triggers = React.useMemo(
    () => [
      { value: 'SCHEDULED', label: 'Scheduled Appointment' },
      { value: 'CHECKED_OUT', label: 'Checked Out' },
    ],
    []
  )
  const triggerIsCheckedOut = React.useMemo(
    () => trigger === 'CHECKED_OUT',
    [trigger]
  )
  const isSendBefore = React.useMemo(
    () => !triggerIsCheckedOut && send === 'days_before_appointment',
    [triggerIsCheckedOut, send]
  )
  const isSendAfter = React.useMemo(
    () => triggerIsCheckedOut && send === 'days_before_appointment',
    [triggerIsCheckedOut, send]
  )
  const sendOptions = React.useMemo(() => {
    return [
      {
        label: `Immediately Upon ${
          triggerIsCheckedOut ? 'Check Out' : 'Scheduling'
        }`,
        value: 'immediately',
      },
      {
        label: `Day(s) ${triggerIsCheckedOut ? 'After' : 'Before'} Appointment`,
        value: 'days_before_appointment',
      },
    ]
  }, [triggerIsCheckedOut])

  React.useEffect(() => {
    const onLoad = async () => {
      await updatePackets()
      await updateForms()
      await syncAppointmentType()
    }
    // eslint-disable-next-line no-console
    onLoad().catch((error) => captureException(error))
    // eslint-disable-next-line
  }, [])

  const validate = React.useCallback(() => {
    const errors = {
      name: '',
      form: '',
      packet: '',
      useForAppointmentTypes: '',
      send: '',
      sendBeforeDays: '',
      sendAfterDays: '',
      trigger: '',
      minDaysBetweenSends: '',
    }
    if (!name) {
      errors.name = 'Name is required'
    }
    if (sendForm && !form && !document) {
      errors.form = 'Form is required'
    }
    if (!sendForm && !packet) {
      errors.packet = 'Packet is required'
    }
    if (!useForAppointmentTypes?.length) {
      errors.useForAppointmentTypes =
        'At least one appointment type is required'
    }
    if (!send) {
      errors.send = 'Send is required'
    }
    if (isSendBefore && sendBeforeDays == null) {
      errors.sendBeforeDays = 'Send before days is required'
    }
    if (isSendAfter && sendAfterDays == null) {
      errors.sendAfterDays = 'Send after days is required'
    }
    if (!trigger) {
      errors.trigger = 'Trigger is required'
    }
    setErrors(errors)
    return !Object.values(errors).some((error) => !!error)
  }, [
    form,
    document,
    isSendAfter,
    isSendBefore,
    name,
    packet,
    send,
    sendAfterDays,
    sendBeforeDays,
    sendForm,
    trigger,
    useForAppointmentTypes?.length,
  ])

  const handleSubmit = React.useCallback(async () => {
    if (!validate()) {
      return
    }

    const data: UpdateCreateWorkflow = {
      name,
      enabled,
      send_upon_scheduling: send === 'immediately',
      send_before: isSendBefore ? sendBeforeDays : null,
      send_after: isSendAfter ? sendAfterDays : null,
      trigger,
      form_id: sendForm ? form : null,
      document_id: sendForm ? document : null,
      packet_id: !sendForm ? packet : null,
      for_appointment_type_ids: useForAppointmentTypes,
      min_days_between_sends: minDaysBetweenSends,
    }
    try {
      if (calendarWorkflow) {
        await updateWorkflow({ id: calendarWorkflow.id, data })
      } else {
        await addWorkflow({ data })
      }
      toast.success('Automation saved successfully')
      onClose()
    } catch (e) {
      toast.error('Failed to save Automation')
    }
  }, [
    validate,
    name,
    enabled,
    send,
    isSendBefore,
    sendBeforeDays,
    isSendAfter,
    sendAfterDays,
    trigger,
    sendForm,
    form,
    document,
    packet,
    useForAppointmentTypes,
    minDaysBetweenSends,
    calendarWorkflow,
    onClose,
    updateWorkflow,
    addWorkflow,
  ])

  return (
    <Modal
      open
      footer={() => undefined}
      maskClosable={false}
      onCancel={onClose}
    >
      <Space size="large">
        <div>
          <Heading>{calendarWorkflow ? 'Edit' : 'Create'} Automation</Heading>
          <Text $light>
            Automated messages are only sent to patients who have a valid email
            address or phone number.
          </Text>
        </div>
        <form>
          <Space size="regular">
            <ToggleField
              compact
              horizontal
              label="Active"
              onChange={() => {
                setEnabled((previous) => !previous)
              }}
              primaryIdle={enabled}
              value={enabled}
            />
            <TextField
              isRequired
              errorLabel={errors.name}
              hasError={!!errors.name}
              label="Automation Name"
              name="name"
              // @ts-expect-error
              onChange={setName}
              value={name}
            />
            <BlockList
              items={[
                {
                  active: sendForm,
                  onClick: () => setSendForm(true),
                  label: 'Send a Form',
                },

                {
                  active: !sendForm,
                  onClick: () => setSendForm(false),
                  label: 'Send a Packet',
                },
              ]}
              size="medium"
            />
            {sendForm === true ? (
              <FormAsyncSelect
                infiniteScroll
                isRequired
                labelInValue
                loadOnMount
                showSearch
                errorLabel={errors.form}
                fetchOptions={loadFormOptions}
                hasError={!!errors.form}
                label="Form to Send"
                onChange={(item) => {
                  if (item.title === 'form') {
                    setForm(String(item.value))
                    setDocument(null)
                  } else {
                    setDocument(String(item.value))
                    setForm(null)
                  }
                }}
                placeholder="Select Form"
                queryKey={[QUERIES.FORMS.LOAD_TO_SELECT]}
                searchKey="search"
                value={{
                  value: form || document || null,
                }}
              />
            ) : (
              <FormAsyncSelect
                infiniteScroll
                isRequired
                loadOnMount
                showSearch
                errorLabel={errors.packet}
                fetchOptions={loadPacketOptions}
                hasError={!!errors.packet}
                label="Packet to Send"
                onChange={(item) => setPacket(String(item))}
                othersParams={{
                  simplified: 'true',
                  without_disabled: 'false',
                  page_size: '50',
                }}
                placeholder="Select Form"
                queryKey={[QUERIES.PACKETS.LOAD_TO_SELECT]}
                searchKey="search"
                value={packet}
              />
            )}
            <Dropdown
              isRequired
              errorLabel={errors.trigger}
              hasError={!!errors.trigger}
              label="Trigger"
              name="trigger"
              onChange={(value) => setTrigger(value as TriggerOptions)}
              options={triggers}
              placeholder="Select Trigger"
              value={trigger}
            />
            <SelectGroup
              block
              isMulti
              // @ts-expect-error
              isRequired
              errorLabel={errors.useForAppointmentTypes}
              hasError={!!errors.useForAppointmentTypes}
              label="Appointment Type(s)"
              loading={appointmentTypesLoading}
              onChange={(values) => {
                setUseForAppointmentTypes(
                  // @ts-expect-error
                  (values?.map((value) => value.value) || []) as string[]
                )
              }}
              options={appointmentTypes?.map((type) => ({
                label: type.name,
                value: type.id,
              }))}
              value={useForAppointmentTypes?.map((id) => ({
                label: appointmentTypes?.find((type) => type.id === id)?.name,
                value: id,
              }))}
            />
            <Dropdown
              isRequired
              errorLabel={errors.send}
              hasError={!!errors.send}
              label="Send"
              name="send"
              onChange={setSend}
              options={sendOptions}
              placeholder="Select Send"
              value={send}
            />
            <div className="flex flex-col gap-2">
              {/* @ts-expect-error */}
              <NumericText
                errorLabel={errors.minDaysBetweenSends}
                hasError={!!errors.minDaysBetweenSends}
                label="Minimum Days Between Automation (optional)"
                minValue={0}
                name="min_days_between_sends"
                onChange={setMinDaysBetweenSends}
                placeholder="#"
                value={minDaysBetweenSends}
              />
              <span className="text-xs text-gray-500">
                This automation will not trigger again if it falls within the
                specified minimum days
              </span>
            </div>
            {send === 'days_before_appointment' && (
              // @ts-expect-error
              <NumericText
                isRequired
                errorLabel={
                  triggerIsCheckedOut
                    ? errors.sendAfterDays
                    : errors.sendBeforeDays
                }
                hasError={
                  triggerIsCheckedOut
                    ? !!errors.sendAfterDays
                    : !!errors.sendBeforeDays
                }
                label="Day(s)"
                maxValue={99}
                minValue={0}
                name="sendBeforeDays"
                onChange={
                  triggerIsCheckedOut ? setSendAfterDays : setSendBeforeDays
                }
                placeholder="#"
                value={triggerIsCheckedOut ? sendAfterDays : sendBeforeDays}
              />
            )}
          </Space>
        </form>
        <Flex $horizontalSpacing="medium" justify="end">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            $primary
            loading={calendarWorkflowsLoading || appointmentTypesLoading}
            onClick={handleSubmit}
          >
            {calendarWorkflow ? 'Update' : 'Create'}
          </Button>
        </Flex>
      </Space>
    </Modal>
  )
}

export default CreateEditAutomationModal
